import React, { useState, useEffect } from 'react';
import { useTrading } from '../context/TradingContext';
import { formatForDisplay } from '../utils/timezone';

function OpenPositions() {
  const { openPositions, systemStatus, loading } = useTrading();
  const [expandedPosition, setExpandedPosition] = useState(null);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-pulse text-dark-text-secondary">Loading positions...</div>
      </div>
    );
  }

  if (!openPositions || openPositions.length === 0) {
    const marketClosed = systemStatus && !systemStatus.within_trading_hours;
    return (
      <div className="flex flex-col items-center justify-center py-10 px-6 text-dark-text-muted text-sm">
        <span>No open positions</span>
        {marketClosed && (
          <span className="text-xs mt-1 text-dark-text-muted/70">Markets are currently closed</span>
        )}
      </div>
    );
  }

  return (
    <div className="divide-y divide-dark-border-subtle">
      {openPositions.map((position) => (
        <PositionRow
          key={position.id}
          position={position}
          isExpanded={expandedPosition === position.id}
          onToggle={() => setExpandedPosition(
            expandedPosition === position.id ? null : position.id
          )}
        />
      ))}
    </div>
  );
}

function PositionRow({ position, isExpanded, onToggle }) {
  const [reasoning, setReasoning] = useState(null);
  const [loadingReasoning, setLoadingReasoning] = useState(false);
  const unrealizedPnL = position.unrealized_pnl || 0;
  const isProfit = unrealizedPnL >= 0;

  // Fetch reasoning when expanded
  useEffect(() => {
    if (isExpanded && !reasoning && position.entry_decision_id) {
      setLoadingReasoning(true);
      fetch(`/api/decisions/${position.entry_decision_id}/reasoning`)
        .then(res => res.ok ? res.json() : null)
        .then(data => setReasoning(data))
        .catch(err => console.error('Error fetching reasoning:', err))
        .finally(() => setLoadingReasoning(false));
    }
  }, [isExpanded, position.entry_decision_id, reasoning]);

  // Calculate duration
  const getDuration = () => {
    if (!position.entry_time) return null;
    const entry = new Date(position.entry_time);
    const now = new Date();
    const diffMs = now - entry;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Calculate distance to SL/TP
  const getSlDistance = () => {
    if (!position.stop_loss || !position.current_price) return null;
    const distance = position.direction === 'LONG'
      ? position.current_price - position.stop_loss
      : position.stop_loss - position.current_price;
    return distance.toFixed(1);
  };

  const getTpDistance = () => {
    if (!position.take_profit || !position.current_price) return null;
    const distance = position.direction === 'LONG'
      ? position.take_profit - position.current_price
      : position.current_price - position.take_profit;
    return distance.toFixed(1);
  };

  // Distance from entry to SL (always negative/risk)
  const getSlDistanceFromEntry = () => {
    if (!position.stop_loss || !position.entry_price) return '0.0';
    const distance = Math.abs(position.entry_price - position.stop_loss);
    return `-${distance.toFixed(1)}`;
  };

  // Distance from entry to TP (always positive/reward)
  const getTpDistanceFromEntry = () => {
    if (!position.take_profit || !position.entry_price) return '0.0';
    const distance = Math.abs(position.take_profit - position.entry_price);
    return distance.toFixed(1);
  };

  // Risk:Reward ratio
  const getRiskRewardRatio = () => {
    if (!position.stop_loss || !position.take_profit || !position.entry_price) return 1;
    const risk = Math.abs(position.entry_price - position.stop_loss);
    const reward = Math.abs(position.take_profit - position.entry_price);
    return risk > 0 ? reward / risk : 1;
  };

  return (
    <div
      className={`transition-all cursor-pointer border-l-4 border-l-transparent hover:bg-dark-tertiary ${
        isProfit ? 'hover:border-l-positive' : 'hover:border-l-negative'
      } ${isExpanded ? 'bg-dark-tertiary ' + (isProfit ? 'border-l-positive' : 'border-l-negative') : ''}`}
      onClick={onToggle}
    >
      {/* Main Row */}
      <div className="px-3 py-2.5 sm:px-5 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Model Info */}
          <div className="min-w-0 w-[90px] sm:w-auto sm:flex-1">
            <div className="font-medium text-xs sm:text-sm text-dark-text truncate">{position.model_name}</div>
            <div className="text-[10px] sm:text-xs text-dark-text-muted">{formatTimeMobile(position.entry_time)}</div>
          </div>

          {/* Direction Arrow Only on Mobile */}
          <div className={`flex items-center justify-center w-6 h-6 sm:w-auto sm:h-auto sm:gap-1 sm:px-2.5 sm:py-1 rounded text-[11px] font-semibold ${
            position.direction === 'LONG'
              ? 'bg-positive/20 text-positive'
              : 'bg-negative/20 text-negative'
          }`}>
            {position.direction === 'LONG' ? (
              <svg className="w-3.5 h-3.5 sm:w-3 sm:h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 sm:w-3 sm:h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            )}
            <span className="hidden sm:inline">{position.direction}</span>
          </div>

          {/* Symbol Badge */}
          <SymbolBadge symbol={position.symbol} />

          {/* Live execution badge */}
          {position.execution_mode === 'LIVE' && <LiveBadge status={position.live_status} />}

          {/* Price Movement */}
          <div className="hidden md:flex items-center gap-2 flex-1 justify-center">
            <span className="font-mono text-sm text-dark-text-secondary">{position.entry_price?.toFixed(1)}</span>
            <span className="text-dark-text-muted">→</span>
            <span className="font-mono text-sm text-dark-text">{position.current_price?.toFixed(1)}</span>
          </div>

          {/* Duration */}
          <div className="hidden lg:block text-xs text-dark-text-muted w-16 text-center">
            {getDuration() || '—'}
          </div>

          {/* Unrealized P/L */}
          <div className={`font-mono font-bold text-right flex-1 sm:flex-none sm:w-[120px] ${
            isProfit ? 'text-positive' : 'text-negative'
          }`}>
            <div className="text-sm sm:text-base">
              {isProfit ? '+' : ''}{unrealizedPnL.toFixed(1)} pts
            </div>
            <div className="text-[10px] sm:text-xs opacity-75">
              {isProfit ? '+$' : '-$'}{Math.abs(unrealizedPnL * (position.dollars_per_point || 0)).toFixed(0)}
            </div>
          </div>

          {/* Chevron */}
          <div className="text-dark-text-muted">
            <svg className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable Details Section */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pb-5 pt-2">
          {/* Price Bar Visualization */}
          {position.stop_loss && position.take_profit && (
            <PriceBarVisualization position={position} />
          )}

          {/* Brief Reasoning */}
          {(loadingReasoning || reasoning?.reasoning || reasoning?.trade_reasoning) && (
            <div className="mt-4 p-3 bg-dark-surface rounded-lg border border-dark-border-subtle">
              {loadingReasoning ? (
                <div className="animate-pulse">
                  <div className="h-3 bg-gray-700 rounded w-3/4"></div>
                </div>
              ) : (
                <p className="text-sm text-dark-text-secondary leading-relaxed">
                  {reasoning?.reasoning || reasoning?.trade_reasoning}
                </p>
              )}
            </div>
          )}

          {/* Two-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
            {/* Risk Management Column */}
            <div>
              <div className="text-xs uppercase tracking-wider text-dark-text-muted border-b border-dark-border pb-2 mb-3">
                Risk Management
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Stop Loss Distance</span>
                  <span className="font-mono text-sm text-negative">
                    {getSlDistanceFromEntry()} pts
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Take Profit Distance</span>
                  <span className="font-mono text-sm text-positive">
                    +{getTpDistanceFromEntry()} pts
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Risk : Reward</span>
                  <div className="flex items-center gap-2">
                    <RiskRewardBar ratio={getRiskRewardRatio()} />
                    <span className="font-mono text-sm text-dark-text">
                      1:{getRiskRewardRatio().toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Position Details Column */}
            <div>
              <div className="text-xs uppercase tracking-wider text-dark-text-muted border-b border-dark-border pb-2 mb-3">
                Position Details
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Position Size</span>
                  <span className="font-mono text-sm text-dark-text">
                    {position.dollars_per_point ? `${position.dollars_per_point.toFixed(2)}/pt` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Risk Amount</span>
                  <span className="font-mono text-sm text-dark-text">
                    {position.position_size_usd ? `$${position.position_size_usd.toLocaleString()}` : '—'}
                    {position.position_size_percent && (
                      <span className="text-dark-text-muted ml-1">({position.position_size_percent}%)</span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Duration</span>
                  <span className="font-mono text-sm text-dark-text">{getDuration() || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Live broker details (real fill / ticket) */}
          {position.execution_mode === 'LIVE' && (
            <div className="mt-5 p-4 rounded-lg border border-accent-gold/30 bg-accent-gold/5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-accent-gold border-b border-accent-gold/20 pb-2 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-gold animate-pulse" />
                Live Execution (real broker fill)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Broker fill (entry)</span>
                  <span className="font-mono text-sm text-dark-text">
                    {(position.broker_open_price ?? position.entry_price)?.toFixed(1) ?? '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Lots</span>
                  <span className="font-mono text-sm text-dark-text">{position.lots != null ? position.lots.toFixed(2) : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Broker ticket</span>
                  <span className="font-mono text-sm text-dark-text">{position.broker_ticket || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Live status</span>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-dark-tertiary text-dark-text border border-dark-border">
                    {position.live_status || '—'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Live trading badge: model executing on the real MT4 account.
function LiveBadge({ status }) {
  const isAlert = status && !['OPEN', 'CLOSED'].includes(status);
  const color = isAlert ? 'bg-negative/20 text-negative' : 'bg-accent-gold/20 text-accent-gold';
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold ${color}`} title={status ? `Live status: ${status}` : 'Live trade'}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      LIVE
    </div>
  );
}

function PriceBarVisualization({ position }) {
  const { stop_loss, entry_price, current_price, take_profit, direction } = position;

  // For LONG: SL on left (red), TP on right (green)
  // For SHORT: TP on left (green), SL on right (red)
  const isLong = direction === 'LONG';

  const leftPrice = isLong ? stop_loss : take_profit;
  const rightPrice = isLong ? take_profit : stop_loss;
  const leftLabel = isLong ? 'Stop Loss' : 'Take Profit';
  const rightLabel = isLong ? 'Take Profit' : 'Stop Loss';
  const leftColor = isLong ? 'text-negative' : 'text-positive';
  const rightColor = isLong ? 'text-positive' : 'text-negative';

  const minPrice = Math.min(leftPrice, rightPrice);
  const maxPrice = Math.max(leftPrice, rightPrice);
  const range = maxPrice - minPrice;

  // Calculate positions as percentages
  const getPosition = (price) => {
    if (range === 0) return 50;
    return ((price - minPrice) / range) * 100;
  };

  const entryPosition = getPosition(entry_price);
  const currentPosition = getPosition(current_price);

  return (
    <div className="bg-dark-surface rounded-lg p-4 border border-dark-border-subtle">
      {/* Price Labels and Values - relative positioning for Entry */}
      <div className="relative mb-4">
        {/* Left and Right labels/values */}
        <div className="flex justify-between text-[10px] uppercase tracking-wider text-dark-text-muted mb-1">
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
        <div className="flex justify-between text-sm font-mono">
          <span className={leftColor}>{leftPrice?.toFixed(1)}</span>
          <span className={rightColor}>{rightPrice?.toFixed(1)}</span>
        </div>

        {/* Entry label positioned above entry marker */}
        <div
          className="absolute top-0 flex flex-col items-center"
          style={{ left: `${entryPosition}%`, transform: 'translateX(-50%)' }}
        >
          <span className="text-[10px] uppercase tracking-wider text-dark-text-muted mb-1">Entry</span>
          <span className="text-sm font-mono text-dark-text-secondary">{entry_price?.toFixed(1)}</span>
        </div>
      </div>

      {/* Visual Bar */}
      <div className="relative h-3 rounded-full overflow-hidden">
        {/* Background gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: isLong
              ? 'linear-gradient(to right, rgba(197, 48, 48, 0.4) 0%, rgba(197, 48, 48, 0.2) 30%, rgba(38, 162, 105, 0.2) 70%, rgba(38, 162, 105, 0.4) 100%)'
              : 'linear-gradient(to right, rgba(38, 162, 105, 0.4) 0%, rgba(38, 162, 105, 0.2) 30%, rgba(197, 48, 48, 0.2) 70%, rgba(197, 48, 48, 0.4) 100%)'
          }}
        />

        {/* Entry marker line */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-dark-text-secondary/50"
          style={{ left: `${entryPosition}%` }}
        />

        {/* Current price indicator (white dot with glow animation) */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full border-2 border-dark-bg shadow-lg animate-pulse-glow"
          style={{ left: `${currentPosition}%`, transform: 'translate(-50%, -50%)' }}
        />
      </div>
    </div>
  );
}

function RiskRewardBar({ ratio }) {
  // ratio is the reward:risk ratio (e.g., 1.4 means 1:1.4)
  // Total is 1 (risk) + ratio (reward)
  const total = 1 + ratio;
  const riskPercent = (1 / total) * 100;
  const rewardPercent = (ratio / total) * 100;

  return (
    <div className="flex h-2 w-16 rounded-sm overflow-hidden">
      <div
        className="bg-negative"
        style={{ width: `${riskPercent}%` }}
      />
      <div
        className="bg-positive"
        style={{ width: `${rewardPercent}%` }}
      />
    </div>
  );
}

function SymbolBadge({ symbol, className = '' }) {
  const getSymbolInfo = (sym) => {
    switch (sym) {
      case '.DE40.':
        return { label: 'DAX', color: 'bg-blue-500/20 text-blue-400' };
      case '.USTEC.':
        return { label: 'NAS', color: 'bg-orange-500/20 text-orange-400' };
      case '.UK100.':
        return { label: 'FTSE', color: 'bg-red-500/20 text-red-400' };
      case '.US30.':
        return { label: 'DOW', color: 'bg-cyan-500/20 text-cyan-400' };
      default:
        return { label: 'US100', color: 'bg-orange-500/20 text-orange-400' };
    }
  };

  const { label, color } = getSymbolInfo(symbol);

  return (
    <div className={`px-2.5 py-1 rounded text-[11px] font-semibold ${color} ${className}`}>
      {label}
    </div>
  );
}

function formatTime(isoString) {
  if (!isoString) return '';
  return formatForDisplay(isoString, {
    includeDate: true,
    includeTime: true,
    includeTimezone: true,
    shortDate: true
  });
}

// Shorter format for mobile: "1/8, 5:30 PM" (no UTC label)
function formatTimeMobile(isoString) {
  if (!isoString) return '';
  return formatForDisplay(isoString, {
    includeDate: true,
    includeTime: true,
    includeTimezone: false,
    shortDate: true
  });
}

export default OpenPositions;
